import { createFileRoute, redirect } from '@tanstack/react-router';
import { AxiosError } from 'axios';

import {
	getListEnvironmentsQueryOptions,
	getListPraetorsQueryOptions,
	getUserQueryKey,
	getUserQueryOptions
} from '@praetoid/client';

import { getCurrentUser, putCurrentUser, removeCurrentUser } from '~/lib/currentUser';
import { router } from '~/lib/router';
import {
	type AppFilterItem,
	type FilterPeriod,
	type GlobalSelection,
	clearGlobals,
	getGlobals,
	setGlobals
} from '~/lib/useGlobalFilter';

import AppLayout from '../../app/layout/AppLayout';

export type AppSearch = GlobalSelection & { log?: string };

export const Route = createFileRoute('/_app')({
	validateSearch: (search): AppSearch => {
		const globalSelection = getGlobals();

		return {
			period: (search.period as FilterPeriod) ?? globalSelection.period,
			praetors: (search.praetors as string[]) ?? globalSelection.praetors,
			filters: (search.filters as AppFilterItem[]) ?? globalSelection.filters,
			start: search.start as string | undefined,
			end: search.end as string | undefined,
			log: typeof search.log === 'string' ? search.log : undefined
		};
	},
	search: {
		middlewares: [
			(ctx) => {
				const result = setGlobals(ctx.search);
				if (result.success) return ctx.next({ ...ctx.search, ...result.data });
				return ctx.next(ctx.search);
			}
		]
	},
	beforeLoad: async ({ context }) => {
		const { queryClient } = context;

		const userOptions = getUserQueryOptions();
		const praetorsOptions = getListPraetorsQueryOptions();
		const environmentsOptions = getListEnvironmentsQueryOptions();

		const userQuery = queryClient.ensureQueryData(userOptions);

		queryClient.ensureQueryData(praetorsOptions);
		queryClient.ensureQueryData(environmentsOptions);

		try {
			const currentUser = getCurrentUser();

			if (currentUser) {
				queryClient.setQueryData(getUserQueryKey(), currentUser);
				void userQuery
					.then(putCurrentUser)
					.catch((error) => handleBackgroundAuthError(error, queryClient));
			} else {
				const user = await userQuery;
				putCurrentUser(user);
			}
		} catch (error) {
			handleForegroundAuthError(error);
		}
	},

	component: function App() {
		return <AppLayout />;
	}
});

const handleForegroundAuthError = (error: unknown): never => {
	if (error instanceof AxiosError && error.response?.status === 401) {
		clearGlobals();
		removeCurrentUser();
		throw redirect({ to: '/signin' });
	}

	throw error;
};

const handleBackgroundAuthError = async (error: unknown, queryClient: { clear: () => void }) => {
	if (error instanceof AxiosError && error.response?.status === 401) {
		queryClient.clear();
		clearGlobals();
		removeCurrentUser();
		await router.navigate({ to: '/signin' });
		return;
	}

	console.error('Background auth validation failed', error);
};
